/**
 * @file quantity.ts
 * @module app/modules/orders-delivery/utils/quantity
 * @description Shared order quantity display helper for the Orders Delivery module.
 * Returns a span node with a consistent label and title for total or processed quantity.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.3.0
 * @version 2.1.2
 */

import React from 'react';
import { OrderQuantityDisplayMode, OrderQuantityLike } from '../types/order-metrics';

/**
 * Normalizes a quantity value, treating null or undefined as 0.
 * @param {number | null | undefined} value - The quantity value to normalize.
 * @returns {number} The normalized quantity value.
 */
const normalizeQuantity = (value: number | null | undefined) => {
  return Number(value ?? 0);
};

/**
 * Returns the total quantity of an order, either from the explicit original ordered quantity or by summing the stock counts of total quantities.
 * @param {OrderQuantityLike} orderVal - The order quantity object containing processed and original ordered quantities.
 * @returns {number} The total quantity of the order.
 */
const getTotalQuantityByCalculatingStockCount = (
  orderVal: OrderQuantityLike
) => {
  return (
    orderVal?.totalQuantities?.reduce(
      (total, item) => total + normalizeQuantity(item?.stockCount),
      0
    ) ?? 0
  );
};

/**
 * Returns a span node with a consistent label and title for total or processed quantity.
 * @param {OrderQuantityLike} orderVal - The order quantity object containing processed and original ordered quantities.
 * @param {string} [className] - Optional additional class name for the span element.
 * @param {OrderQuantityDisplayMode} [mode='total'] - The display mode, either 'total' or 'processed'.
 * @returns {React.ReactElement} A React span element displaying the appropriate quantity.
 */
const createOrderQuantitySpan = (
  className: string | undefined,
  title: string | undefined,
  children: string | number
) => {
  return React.createElement(
    'span',
    {
      className: `${className ?? ''} order-qty`,
      title,
    },
    children
  );
};

/**
 * Returns a span node using the original total-items logic by summing stockCount values.
 * @param {OrderQuantityLike} orderVal - The order quantity object containing total quantities.
 * @param {string} [className] - Optional additional class name for the span element.
 * @returns {React.ReactElement} A React span element displaying the summed item count.
 */
export const getOrderItemsByCalculatingStockCountDisplay = (
  orderVal: OrderQuantityLike,
  className?: string
) => {
  const totalQuantity = getTotalQuantityByCalculatingStockCount(orderVal);

  return createOrderQuantitySpan(
    className,
    `total order ${totalQuantity} quantity`,
    totalQuantity
  );
};